import React, { useState } from 'react';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import logger from '@/utils/logger';
import { useQueryClient } from '@tanstack/react-query';

// Step components
import AddPetStep from './steps/AddPetStep';
import UploadDocumentStep from './steps/UploadDocumentStep';
import SetReminderStep from './steps/SetReminderStep';
import CompletionStep from './steps/CompletionStep';

interface OnboardingTourProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * The main onboarding tour component that displays a 3-step guided tour
 * for first-time users of PetDocument.
 */
const OnboardingTour: React.FC<OnboardingTourProps> = ({ 
  isOpen, 
  onClose 
}: OnboardingTourProps): React.ReactElement => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [selectedPetId, setSelectedPetId] = useState<string | null>(null);
  const { updateUserMetadata } = useAuth();
  const queryClient = useQueryClient();

  // Handle completing or skipping the tour
  const handleCompleteTour = async (): Promise<void> => {
    try {
      // Mark the onboarding as completed in Supabase metadata
      const updatedUser = await updateUserMetadata({ has_completed_onboarding: true });
      
      if (updatedUser) {
        logger.info('Onboarding tour completed');
        
        // Invalidate relevant queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['user'] });
        
        // Show welcome toast when completed normally (not skipped)
        if (currentStep === 4) {
          setTimeout(() => {
            toast.success('Welcome aboard! You can now manage your pets with ease.');
          }, 500);
        }
        
        onClose();
      } else {
        throw new Error('Failed to update user metadata');
      }
    } catch (error) {
      logger.error('Error updating onboarding status:', error);
      toast.error('There was an error saving your progress. Please try again.');
    }
  };

  // Navigation between steps
  const nextStep = (): void => setCurrentStep((prev) => Math.min(prev + 1, 4));
  const prevStep = (): void => setCurrentStep((prev) => Math.max(prev - 1, 1));

  // Called when a pet is added successfully
  const handlePetAdded = (petId: string): void => {
    setSelectedPetId(petId);
    nextStep();
  };

  // Called when a document is uploaded successfully
  const handleDocumentUploaded = (): void => {
    nextStep();
  };
  
  // Called when a reminder is set successfully
  const handleReminderSet = (): void => {
    nextStep();
  };

  // Animation classes for transitions
  const getStepClasses = (step: number): string => {
    return `transform transition-all duration-300 ${
      currentStep === step 
        ? 'translate-x-0 opacity-100' 
        : currentStep < step 
          ? 'translate-x-20 opacity-0 absolute' 
          : '-translate-x-20 opacity-0 absolute'
    }`;
  };

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => !open && handleCompleteTour()}
    >
      <DialogContent className="sm:max-w-md p-0 overflow-hidden sm:overflow-visible sm:h-auto max-h-[90vh] sm:max-h-none">
        <DialogClose className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>

        {/* Step indicator */}
        <div className="flex justify-center items-center gap-2 pt-6 px-6">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex items-center">
              <div
                className={`rounded-full w-8 h-8 flex items-center justify-center ${
                  currentStep === step
                    ? step === 4 ? 'bg-green-500 text-white' : 'bg-primary text-white'
                    : step < currentStep
                    ? 'bg-gray-200 text-gray-700'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step === 4 && currentStep === 4 ? (
                  <Check className="h-4 w-4" />
                ) : (
                  step
                )}
              </div>
              {step < 4 && (
                <div className="h-[1px] w-10 bg-gray-300 mx-1"></div>
              )}
            </div>
          ))}
        </div>

        {/* Step content - desktop has NO scroll, mobile only has scroll */}
        <div className="p-6 relative min-h-[350px] overflow-hidden sm:overflow-visible sm:h-auto max-sm:overflow-y-auto">
          <div className={getStepClasses(1)}>
            {currentStep === 1 && (
              <AddPetStep onPetAdded={handlePetAdded} />
            )}
          </div>
          
          <div className={getStepClasses(2)}>
            {currentStep === 2 && (
              <UploadDocumentStep
                petId={selectedPetId}
                onDocumentUploaded={handleDocumentUploaded}
              />
            )}
          </div>
          
          <div className={getStepClasses(3)}>
            {currentStep === 3 && (
              <SetReminderStep 
                petId={selectedPetId} 
                onReminderSet={handleReminderSet}
              />
            )}
          </div>

          <div className={getStepClasses(4)}>
            {currentStep === 4 && (
              <CompletionStep onComplete={handleCompleteTour} />
            )}
          </div>
        </div>

        {/* Navigation buttons - desktop normal, mobile fixed */}
        <div className="flex justify-between p-6 border-t sm:relative sm:bg-transparent max-sm:sticky max-sm:bottom-0 max-sm:bg-white max-sm:z-10">
          <div>
            {/* Only show Skip Tour or Back button for steps 1-3 */}
            {currentStep < 4 && (
              currentStep === 1 ? (
                <Button variant="ghost" onClick={handleCompleteTour} size="sm" className="text-gray-500">
                  Skip Tour
                </Button>
              ) : (
                <Button variant="outline" onClick={prevStep} size="sm">
                  Back
                </Button>
              )
            )}
          </div>
          
          <div>
            {/* Only show navigation options for steps 1-3 */}
            {currentStep === 3 && (
              <Button onClick={nextStep} size="sm">
                Next
              </Button>
            )}
            {currentStep !== 3 && currentStep !== 1 && currentStep < 4 && (
              <Button variant="ghost" onClick={handleCompleteTour} size="sm" className="text-gray-500">
                Skip Tour
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingTour;
